import React from 'react';
import { X, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { learnMoreContent } from './learnMoreContent';

interface LearnMoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  language: string;
}

const LearnMoreModal: React.FC<LearnMoreModalProps> = ({ isOpen, onClose, language }) => {
  if (!isOpen) return null;

  const content = learnMoreContent[language as keyof typeof learnMoreContent] || learnMoreContent.lt;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">{content.title}</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          {/* Main sections */}
          <div className="grid md:grid-cols-2 gap-6">
            {content.sections.map((section, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="text-3xl">{section.icon}</div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        {section.heading}
                      </h3>
                      {section.text && (
                        <p className="text-gray-600 leading-relaxed">
                          {section.text}
                        </p>
                      )}
                      {section.list && (
                        <ul className="space-y-2 mt-3">
                          {section.list.map((item, itemIndex) => (
                            <li key={itemIndex} className="flex items-start gap-2">
                              <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-600 text-sm">{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Premium section */}
          <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-6 border border-emerald-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
              {content.premiumTitle}
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {content.premiumFeatures.map((feature, index) => (
                <div key={index} className="flex items-center gap-3 bg-white/80 rounded-lg p-3">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                  <span className="text-gray-700 text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center space-y-4">
            <p className="text-gray-600">
              {language === 'lt' 
                ? 'Pradėkite nemokamai ir pamatykite skirtumą!'
                : 'Start for free and see the difference!'
              }
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                size="lg" 
                className="bg-emerald-600 hover:bg-emerald-700 px-8"
                onClick={onClose}
              >
                {language === 'lt' ? 'Pradėti nemokamai' : 'Start for Free'}
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 px-8"
                onClick={onClose}
              >
                {language === 'lt' ? 'Supratau' : 'Got it'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearnMoreModal; 